import { createFileRoute } from "@tanstack/react-router";

import { LegalDocLayout, type LegalTocItem } from "@/components/legal/LegalDocLayout";
import {
  PRIVACY_LAST_UPDATED,
  PRIVACY_PREAMBLE,
  PRIVACY_SECTIONS,
  type PrivacyBlock,
} from "@/content/privacy";

import { seoHead } from "@/lib/seo";

export const Route = createFileRoute("/privacy")({
  head: () => seoHead({
    title: "プライバシーポリシー - KAGEM",
    description: "KAGEMのプライバシーポリシー。個人情報保護法に基づく取扱い、第三者提供、越境移転について定めています。",
    path: "/privacy",
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  const toc: LegalTocItem[] = PRIVACY_SECTIONS.map((s) => ({
    id: s.id,
    label: `${s.number}. ${s.title}`,
  }));

  return (
    <LegalDocLayout
      title="プライバシーポリシー"
      preamble={PRIVACY_PREAMBLE}
      lastUpdated={PRIVACY_LAST_UPDATED}
      toc={toc}
    >
      {PRIVACY_SECTIONS.map((section) => (
        <section key={section.id} id={section.id} className="legal-section scroll-mt-24">
          <h2 className="mb-3 text-lg font-semibold sm:text-xl">
            {section.number}. {section.title}
          </h2>
          {section.blocks.map((block, i) => (
            <BlockRenderer key={i} block={block} />
          ))}
        </section>
      ))}
    </LegalDocLayout>
  );
}

function BlockRenderer({ block }: { block: PrivacyBlock }) {
  if (block.type === "p") {
    return <p className="mb-3 text-sm leading-relaxed text-foreground/90">{block.text}</p>;
  }

  if (block.type === "subsection") {
    return (
      <div className="mb-4 mt-4">
        <h3 className="mb-2 text-base font-semibold text-foreground">{block.heading}</h3>
        {block.blocks.map((b, i) => (
          <BlockRenderer key={i} block={b} />
        ))}
      </div>
    );
  }

  // List (ul or ol)
  const ListTag = block.type === "ol" ? "ol" : "ul";
  const listClass =
    block.type === "ol"
      ? "mb-3 ml-5 list-decimal space-y-1.5 text-sm leading-relaxed text-foreground/90 marker:text-muted-foreground"
      : "mb-3 ml-5 list-disc space-y-1.5 text-sm leading-relaxed text-foreground/90 marker:text-muted-foreground";

  return (
    <ListTag className={listClass}>
      {block.items.map((item, i) => {
        if (typeof item === "string") {
          return <li key={i}>{item}</li>;
        }
        const isExternal = (href: string) => /^https?:\/\//.test(href);
        return (
          <li key={i}>
            {item.text}
            {item.links && item.links.length > 0 && (
              <>
                {" "}
                {item.links.map((link, j) => (
                  <span key={j}>
                    {j > 0 && " / "}
                    <a
                      href={link.href}
                      target={isExternal(link.href) ? "_blank" : undefined}
                      rel={isExternal(link.href) ? "noopener noreferrer" : undefined}
                      className="text-primary underline-offset-2 hover:underline"
                    >
                      {link.label}
                    </a>
                  </span>
                ))}
              </>
            )}
          </li>
        );
      })}
    </ListTag>
  );
}
